import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import swal from 'sweetalert';
import { allReturnOrderByUserAction, clearErrorAction } from '../../redux/actions/orderAction';
import Navbar from '../HomeScreen/Navbar';
import Footer from '../HomeScreen/bottomsection/Footer';
import Loader from '../../layout/loader';
import MetaTag from '../../layout/MetaTag';
import EmptyReturnOrder from './EmptyRetrunOrder';

import AssignmentReturnOutlinedIcon from '@mui/icons-material/AssignmentReturnOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import '../../css/returnOrders.css';

function AllReturnsOrdersByUser() {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.user);
  const { error, loading, returnOrders } = useSelector((state) => state.allReturnOrderByUser);

  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'processing' | 'scheduled' | 'completed'
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (error) {
      swal({
        title: 'Error',
        text: error,
        icon: 'error',
      });
      dispatch(clearErrorAction());
    }

    dispatch(allReturnOrderByUserAction());
  }, [dispatch, error]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Pending Confirmation';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return String(dateString).substring(0, 10);
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return String(dateString).substring(0, 10);
    }
  };

  // Helper to categorize return status
  const getStatusCategory = (statusStr) => {
    if (!statusStr) return 'processing';
    const s = statusStr.toLowerCase();
    if (s.includes('complete') || s.includes('receive') || s.includes('refund')) {
      return 'completed';
    }
    if (s.includes('accept') || s.includes('approve') || s.includes('pickup') || s.includes('schedule')) {
      return 'scheduled';
    }
    if (s.includes('reject')) {
      return 'rejected';
    }
    return 'processing';
  };

  // Counts for tabs
  const tabCounts = useMemo(() => {
    const counts = { all: 0, processing: 0, scheduled: 0, completed: 0 };
    if (!returnOrders || !Array.isArray(returnOrders)) return counts;

    counts.all = returnOrders.length;
    returnOrders.forEach((item) => {
      const cat = getStatusCategory(item.returnInformation?.status);
      if (cat === 'processing') counts.processing++;
      else if (cat === 'scheduled') counts.scheduled++;
      else if (cat === 'completed') counts.completed++;
    });
    return counts;
  }, [returnOrders]);

  // Filtered return items
  const filteredOrders = useMemo(() => {
    if (!returnOrders || !Array.isArray(returnOrders)) return [];

    let list = returnOrders;

    // Filter by tab
    if (activeTab !== 'all') {
      list = list.filter((item) => {
        const cat = getStatusCategory(item.returnInformation?.status);
        return cat === activeTab;
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((item) => {
        const idMatch = String(item._id || '').toLowerCase().includes(q);
        const orderIdMatch = String(item.orderId || '').toLowerCase().includes(q);
        const reasonMatch = String(item.returnReason || '').toLowerCase().includes(q);
        const typeMatch = String(item.returnType || '').toLowerCase().includes(q);
        return idMatch || orderIdMatch || reasonMatch || typeMatch;
      });
    }

    return list;
  }, [returnOrders, activeTab, searchQuery]);

  return (
    <Fragment>
      <MetaTag
        title={`Returns & Exchanges | IM Online Shopping`}
        description="Track all your return and exchange orders, doorstep pickup schedules, and refund status on IM Online Shopping."
      />

      <div className="ret-page-wrapper">
        <Navbar />

        <main className="ret-container">
          {/* Breadcrumbs */}
          <nav className="ret-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="ret-breadcrumb-sep">/</span>
            <Link to="/userprofile">My Account</Link>
            <span className="ret-breadcrumb-sep">/</span>
            <span className="ret-breadcrumb-current">Returns & Exchange</span>
          </nav>

          {/* Hero Header */}
          <section className="ret-hero">
            <div className="ret-hero-left">
              <div className="ret-hero-icon-box">
                <AssignmentReturnOutlinedIcon />
              </div>
              <div className="ret-hero-text">
                <h1>Returns & Exchanges</h1>
                <p>
                  Manage and track return requests, doorstep pickup schedules, and refund settlements for {user?.firstName ? `${user.firstName}'s account` : 'your orders'}.
                </p>
                <div className="ret-policy-chips">
                  <span className="ret-chip">
                    <VerifiedUserOutlinedIcon /> 7-Day Easy Returns
                  </span>
                  <span className="ret-chip">
                    <LocalShippingOutlinedIcon /> Free Doorstep Pickup
                  </span>
                  <span className="ret-chip">
                    <CheckCircleOutlineIcon /> Fast Direct Refund
                  </span>
                </div>
              </div>
            </div>

            <div className="ret-hero-actions">
              <Link to="/orders/Details/me" className="ret-btn-new">
                <AutorenewOutlinedIcon fontSize="small" />
                <span>Return Item from Orders</span>
              </Link>
              <Link to="/userprofile" className="ret-btn-back">
                <ArrowBackIosNewOutlinedIcon style={{ fontSize: '12px' }} />
                <span>Back to Profile</span>
              </Link>
            </div>
          </section>

          {loading ? (
            <Loader />
          ) : !returnOrders || returnOrders.length === 0 ? (
            <EmptyReturnOrder />
          ) : (
            <Fragment>
              {/* Filter Tabs & Search */}
              <div className="ret-filter-bar">
                <div className="ret-status-tabs" role="tablist">
                  <button
                    type="button"
                    className={`ret-tab-pill ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    <span>All Requests</span>
                    <span className="ret-tab-badge">{tabCounts.all}</span>
                  </button>

                  <button
                    type="button"
                    className={`ret-tab-pill ${activeTab === 'processing' ? 'active' : ''}`}
                    onClick={() => setActiveTab('processing')}
                  >
                    <span>Processing</span>
                    <span className="ret-tab-badge">{tabCounts.processing}</span>
                  </button>

                  <button
                    type="button"
                    className={`ret-tab-pill ${activeTab === 'scheduled' ? 'active' : ''}`}
                    onClick={() => setActiveTab('scheduled')}
                  >
                    <span>Pickup Scheduled</span>
                    <span className="ret-tab-badge">{tabCounts.scheduled}</span>
                  </button>

                  <button
                    type="button"
                    className={`ret-tab-pill ${activeTab === 'completed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('completed')}
                  >
                    <span>Completed</span>
                    <span className="ret-tab-badge">{tabCounts.completed}</span>
                  </button>
                </div>

                <div className="ret-search-wrap">
                  <SearchIcon className="ret-search-icon" />
                  <input
                    type="text"
                    className="ret-search-input"
                    placeholder="Search by Return or Order ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* Returns List */}
              {filteredOrders.length === 0 ? (
                <div className="ret-empty-card" style={{ padding: '36px 20px' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '17px', color: '#111315', fontWeight: '800' }}>
                    No matching returns found
                  </h4>
                  <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
                    Try clearing your search query or selecting a different status filter tab.
                  </p>
                  <button
                    type="button"
                    className="ret-btn-back"
                    onClick={() => {
                      setSearchQuery('');
                      setActiveTab('all');
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
              ) : (
                <div className="ret-list">
                  {filteredOrders.map((item) => {
                    const statusStr = item.returnInformation?.status || 'Processing';
                    const category = getStatusCategory(statusStr);
                    const formattedReturnId = String(item._id).slice(-8).toUpperCase();
                    const formattedOrderId = String(item.orderId).slice(-8).toUpperCase();
                    const createdAtFormatted = formatDate(item.returnInformation?.createdAt);
                    const pickupDateFormatted = formatDate(item.returnAccpetAt);

                    return (
                      <article key={item._id} className="ret-card">
                        {/* Header */}
                        <div className="ret-card-header">
                          <div className="ret-card-meta-left">
                            <span className="ret-id-badge" title={`Full Return ID: ${item._id}`}>
                              REF #{formattedReturnId}
                            </span>
                            <Link
                              to={`/order/${item.orderId}`}
                              className="ret-order-link"
                              title="Click to view original order details & invoice"
                            >
                              <span>Order #{formattedOrderId}</span>
                              <LaunchIcon />
                            </Link>
                            <span className="ret-date-text">
                              Requested on {createdAtFormatted}
                            </span>
                          </div>

                          <div className={`ret-status-badge ${category}`}>
                            {category === 'processing' && <AccessTimeOutlinedIcon style={{ fontSize: '14px' }} />}
                            {category === 'scheduled' && <LocalShippingOutlinedIcon style={{ fontSize: '14px' }} />}
                            {category === 'completed' && <CheckCircleOutlineIcon style={{ fontSize: '14px' }} />}
                            <span>{statusStr}</span>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="ret-card-body">
                          <div className="ret-type-row">
                            <span className="ret-type-tag">
                              <AutorenewOutlinedIcon style={{ fontSize: '15px' }} />
                              {item.returnType === 'Exchange' ? 'Size / Item Exchange' : 'Return & Full Refund'}
                            </span>

                            <span className="ret-pickup-info">
                              <LocalShippingOutlinedIcon />
                              Doorstep Pickup: {pickupDateFormatted}
                            </span>
                          </div>

                          {/* Reason */}
                          {item.returnReason && (
                            <div className="ret-reason-box">
                              <span className="ret-reason-label">Reason for Request</span>
                              <p className="ret-reason-content">{item.returnReason}</p>
                            </div>
                          )}

                          {/* Uploaded Images Gallery if any */}
                          {item.returnItemImages && item.returnItemImages.length > 0 && (
                            <div className="ret-images-wrap">
                              <span className="ret-images-title">Uploaded Item Photos</span>
                              <div className="ret-images-gallery">
                                {item.returnItemImages.map((imgObj, imgIdx) => (
                                  <a
                                    key={imgObj.public_id || imgIdx}
                                    href={imgObj.imageurl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Click to view full photo"
                                  >
                                    <img
                                      src={imgObj.imageurl}
                                      alt={`Item inspection ${imgIdx + 1}`}
                                      className="ret-thumb-img"
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="ret-card-footer">
                          <div className="ret-card-actions-left">
                            <Link to={`/order/${item.orderId}`} className="ret-action-link primary">
                              <LaunchIcon style={{ fontSize: '14px' }} />
                              <span>View Order & Tracking</span>
                            </Link>
                            <Link to="/about/return-and-exchange" className="ret-action-link">
                              <span>Return Guidelines</span>
                            </Link>
                          </div>

                          <Link to="/user/complain/request" className="ret-action-link">
                            <SupportAgentOutlinedIcon style={{ fontSize: '15px' }} />
                            <span>Contact Support</span>
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {/* 3-Step Process Guide */}
              <section className="ret-process-section" aria-label="How returns work">
                <h3 className="ret-process-heading">
                  <VerifiedUserOutlinedIcon style={{ color: 'var(--ret-brand)' }} />
                  <span>How IM Online Returns & Exchanges Work</span>
                </h3>
                <div className="ret-process-grid">
                  <div className="ret-process-step">
                    <span className="ret-step-num">1</span>
                    <h4 className="ret-step-title">Select Item in Orders</h4>
                    <p className="ret-step-desc">
                      Go to your delivered orders, choose the item you'd like to return or exchange within 7 days, and tell us the reason.
                    </p>
                  </div>

                  <div className="ret-process-step">
                    <span className="ret-step-num">2</span>
                    <h4 className="ret-step-title">Free Doorstep Pickup</h4>
                    <p className="ret-step-desc">
                      Our courier partner visits your address, inspects the item with tags intact, and securely collects the parcel.
                    </p>
                  </div>

                  <div className="ret-process-step">
                    <span className="ret-step-num">3</span>
                    <h4 className="ret-step-title">Fast Refund or Replacement</h4>
                    <p className="ret-step-desc">
                      Once picked up, your refund is directly credited back to your bank account or payment method within 24-48 hours.
                    </p>
                  </div>
                </div>
              </section>
            </Fragment>
          )}
        </main>

        <Footer />
      </div>
    </Fragment>
  );
}

export default AllReturnsOrdersByUser;